import { API_URL as API, BACKEND_URL } from '../utils/config';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Check, Loader } from 'lucide-react';


const CheckoutSuccess = () => {
  const { t } = useLanguage();
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      navigate('/cart');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await clearCart();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-lg mx-auto px-6 text-center">
        {status === 'checking' && (
          <div data-testid="checking-status">
            <Loader className="w-16 h-16 mx-auto mb-6 animate-spin text-[#C6A87C]" />
            <h1 className="text-3xl font-serif text-[#1A1A1A] mb-4">
              {t('checkingPayment') || 'Checking payment status...'}
            </h1>
            <p className="text-[#8A8A8A]">
              {t('pleaseWait') || 'Please wait while we confirm your payment'}
            </p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="success-status">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl md:text-5xl font-serif text-[#1A1A1A] mb-6">
              {t('paymentSuccessful')}
            </h1>
            <p className="text-[#8A8A8A] mb-8">
              {t('orderConfirmation') || 'Thank you for your order! You will receive a confirmation email shortly.'}
            </p>
            <button
              onClick={() => navigate('/shop')}
              className="bg-[#1A1A1A] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#333333] transition-all duration-300"
              data-testid="continue-shopping-btn"
            >
              {t('continueShopping')}
            </button>
          </div>
        )}

        {status === 'expired' && (
          <div data-testid="expired-status">
            <h1 className="text-3xl font-serif text-[#991B1B] mb-6">
              {t('paymentExpired') || 'Payment Session Expired'}
            </h1>
            <p className="text-[#8A8A8A] mb-8">
              {t('tryAgain') || 'Your payment session has expired. Please try again.'}
            </p>
            <button
              onClick={() => navigate('/cart')}
              className="bg-[#1A1A1A] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#333333] transition-all duration-300"
            >
              {t('backToCart') || 'Back to Cart'}
            </button>
          </div>
        )}

        {(status === 'error' || status === 'timeout') && (
          <div data-testid="error-status">
            <h1 className="text-3xl font-serif text-[#991B1B] mb-6">
              {t('errorOccurred') || 'An Error Occurred'}
            </h1>
            <p className="text-[#8A8A8A] mb-8">
              {t('contactSupport') || 'Please contact support or check your email for order confirmation.'}
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="bg-[#1A1A1A] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#333333] transition-all duration-300"
            >
              {t('contactUs')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;